import { Component, Project } from "projen";
import { BuildWorkflow } from "projen/lib/build";
import { WorkflowSteps } from "projen/lib/github";
import { JobPermission, JobStep } from "projen/lib/github/workflows-model";

/**
 * Job id of the report job that merges every target's captured diff into one
 * reviewable summary and artifact.
 */
export const DIFF_REPORT_JOB_ID = "diff-report";

/**
 * Directory each target's diff is captured to, and the directory the report job
 * merges every downloaded part into.
 */
export const DIFF_OUTPUT_DIRECTORY = "cdk-diff";

/**
 * Artifact-name prefix for a single target's captured diff.
 *
 * These are intermediates: the diff jobs run in parallel and
 * `actions/upload-artifact` v4+ treats artifact names as immutable, so each
 * target must upload under its own name. The report job merges them into
 * {@link DIFF_ARTIFACT_NAME}, which is the one meant to be read.
 */
export const DIFF_PART_ARTIFACT_PREFIX = "cdk-diff-part";

/**
 * Name of the single merged artifact carrying every target's diff.
 */
export const DIFF_ARTIFACT_NAME = "cdk-diff";

/**
 * Line prefix a diff job writes at the head of its capture when a matched stack
 * will be created from scratch — it does not exist, or exists only as a
 * failed-create tombstone.
 *
 * Deliberately plain ASCII and anchored at the start of a line: the diff job
 * writes it and the report job greps for it, so it doubles as the machine
 * signal that lets the report surface a from-scratch target outside its
 * collapsed `<details>` block.
 */
export const DIFF_NEW_STACK_MARKER = "NEW STACK";

/**
 * Retention for the per-target parts. They exist only to reach the report job
 * in the same run, so they are kept for the minimum GitHub allows rather than
 * cluttering the run's artifact list for the full retention period.
 */
const DIFF_PART_RETENTION_DAYS = 1;

/**
 * Byte budget for the diff text embedded in `$GITHUB_STEP_SUMMARY`.
 *
 * GitHub caps a step summary at 1 MiB and silently drops the whole thing when
 * the cap is exceeded. The cap is per step and the report renders every target
 * in one step, so this budget is split across them — a single noisy target must
 * not be able to cost every other target its summary.
 */
const DIFF_SUMMARY_BYTE_LIMIT = 900000;

/**
 * One target's contribution to the diff report.
 */
export interface DiffReportTarget {
  /**
   * Human-readable target label, used as the summary heading.
   */
  readonly label: string;

  /**
   * Job id of the target's own diff job. Becomes a `needs` entry on the report.
   */
  readonly jobName: string;

  /**
   * File the target's diff lands at once the parts are merged.
   */
  readonly outputFile: string;

  /**
   * Branch filter this target deploys under, if any.
   */
  readonly branchCondition?: string;
}

/**
 * Inputs to {@link DiffReportJob.attach}.
 */
export interface DiffReportJobAttachOptions {
  /**
   * Plan workflow the report belongs to. One report job per build workflow.
   */
  readonly buildWorkflow: BuildWorkflow;

  /**
   * Rendered repository-guard expression, if the workflow is pinned.
   */
  readonly homeRepositoryCondition?: string;

  /**
   * Render the merged diffs into the job summary.
   */
  readonly summary: boolean;

  /**
   * Upload the merged diffs as a single run artifact.
   */
  readonly artifact: boolean;

  /**
   * Targets the attaching component contributes.
   */
  readonly targets: Array<DiffReportTarget>;
}

/**
 * The run URL, assembled entirely from runtime context so the generated
 * workflow keeps working when a consumer repo is forked, mirrored, or cloned
 * into another organization.
 */
const RUN_URL =
  "${{ github.server_url }}/${{ github.repository }}/actions/runs/${{ github.run_id }}";

/**
 * The job that turns a plan workflow's per-target `cdk diff` jobs into one
 * reviewable summary and one artifact.
 *
 * The diffs themselves stay in their own jobs, in parallel — a diff is a
 * read-only comparison and there is no reason to serialize them, and each needs
 * its own account's credentials anyway. What does not scale is the *review*:
 * under the `plan-apply` gate the diff is the only review surface, so a
 * seven-service repo would otherwise mean seven job summaries to open and
 * reconcile by hand. This job downloads every target's part, renders them under
 * one heading each, and uploads the merged directory as a single artifact.
 *
 * Every deploy component sharing the build workflow contributes here, so the
 * report spans services rather than stopping at one.
 *
 * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
 */
export class DiffReportJob extends Component {
  public static of(
    project: Project,
    buildWorkflow: BuildWorkflow,
  ): DiffReportJob | undefined {
    const isDefined = (c: Component): c is DiffReportJob =>
      c instanceof DiffReportJob && c.buildWorkflow === buildWorkflow;
    return project.components.find(isDefined);
  }

  /**
   * Add a component's targets to the plan workflow's diff report, creating the
   * report job the first time it is called for that workflow.
   */
  public static attach(
    project: Project,
    options: DiffReportJobAttachOptions,
  ): DiffReportJob {
    const existing = DiffReportJob.of(project, options.buildWorkflow);

    if (existing) {
      existing.record(options);
      return existing;
    }

    return new DiffReportJob(project, options);
  }

  /**
   * Plan workflow this report belongs to.
   */
  public readonly buildWorkflow: BuildWorkflow;

  private readonly homeRepositoryCondition?: string;

  /**
   * Every contributed target, in attach order.
   */
  private readonly targets: Array<DiffReportTarget> = [];

  /**
   * Publish settings are OR-ed rather than first-wins: one report means one
   * answer, and a component that asked for a summary should get one even when
   * a sibling did not.
   */
  private summary = false;

  private artifact = false;

  constructor(project: Project, options: DiffReportJobAttachOptions) {
    super(project);

    this.buildWorkflow = options.buildWorkflow;
    this.homeRepositoryCondition = options.homeRepositoryCondition;
    this.record(options);

    /**
     * Registered on the workflow directly rather than through
     * `addPostBuildJob`, which would prepend a download of the build artifact
     * this job has no use for — it consumes the diff parts, not the assembly.
     * The per-target diff jobs remain post-build jobs, so the invariant that
     * keeps projen uploading the build artifact is unaffected.
     *
     * Registration has to happen now rather than at `preSynthesize`, because
     * `addBuildCompleteJob` enumerates the workflow's jobs at the end of the
     * `AwsDeployWorkflow` constructor to build the aggregate gate's `needs`. A
     * job added later would be missing from that gate.
     */
    this.buildWorkflow.workflow.addJob(DIFF_REPORT_JOB_ID, {
      name: "Diff report",
      needs: ["build"],
      runsOn: ["ubuntu-latest"],
      permissions: {
        contents: JobPermission.READ,
      },
      steps: [],
    });
  }

  preSynthesize(): void {
    this.buildWorkflow.workflow.updateJob(DIFF_REPORT_JOB_ID, {
      name: "Diff report",
      /**
       * `build` is a direct dependency so the job can read its
       * self-mutation output, which is otherwise unreachable — the `needs`
       * context only carries direct dependencies, not transitive ones.
       */
      needs: [
        "build",
        ...Array.from(new Set(this.targets.map((target) => target.jobName))),
      ],
      runsOn: ["ubuntu-latest"],
      permissions: {
        contents: JobPermission.READ,
      },
      if: this.renderJobCondition(),
      steps: [
        ...this.renderDownloadStep(),
        ...this.renderSummaryStep(),
        ...this.renderUploadStep(),
        ...this.renderFailureStep(),
      ],
    });

    super.preSynthesize();
  }

  /**
   * Fold one component's contribution in.
   */
  private record = (options: DiffReportJobAttachOptions): void => {
    this.targets.push(...options.targets);
    this.summary = this.summary || options.summary;
    this.artifact = this.artifact || options.artifact;
  };

  /**
   * Compose the report's own condition.
   *
   * `!cancelled()` leads, and it is load-bearing: a job whose `needs` failed or
   * were skipped is skipped by default, so without a status function the report
   * would vanish in exactly the cases it is most wanted — one target's diff
   * failing, or some targets not applying to this branch.
   */
  private renderJobCondition = (): string => {
    const branchConditions = this.targets.map(
      (target) => target.branchCondition,
    );
    const anyBranch = branchConditions.some((condition) => !condition);
    const branchClause = anyBranch
      ? undefined
      : Array.from(new Set(branchConditions)).join(" || ");

    return (
      "${{ " +
      [
        "!cancelled()",
        "!needs.build.outputs.self_mutation_happened",
        ...(this.homeRepositoryCondition ? [this.homeRepositoryCondition] : []),
        ...(branchClause ? [`(${branchClause})`] : []),
      ].join(" && ") +
      " }}"
    );
  };

  /**
   * Pull every target's part into one directory.
   *
   * `continueOnError` because a run where every target's diff job was skipped
   * by its branch filter matches no parts at all, and a report with nothing to
   * report is not a failure.
   */
  private renderDownloadStep = (): Array<JobStep> => {
    return [
      WorkflowSteps.downloadArtifact({
        name: "Download target diffs",
        continueOnError: true,
        with: {
          pattern: `${DIFF_PART_ARTIFACT_PREFIX}-*`,
          mergeMultiple: true,
          path: DIFF_OUTPUT_DIRECTORY,
        },
      }),
    ];
  };

  /**
   * Render every target's diff into the job summary, one collapsed `<details>`
   * block per target.
   *
   * A target whose part is absent renders as "did not run", which is what
   * distinguishes a target that was skipped or whose diff failed before it
   * captured anything from one that simply had no changes — the latter has a
   * file, carrying the CDK CLI's own no-differences wording, rather than being
   * detected by matching on it.
   *
   * A capture carrying {@link DIFF_NEW_STACK_MARKER} lines has its banners
   * lifted out of the `<details>` block and quoted under the heading, so a
   * target that will be created from scratch reads as such without expanding
   * anything.
   */
  private renderSummaryStep = (): Array<JobStep> => {
    if (!this.summary || this.targets.length === 0) {
      return [];
    }

    const budget = Math.floor(DIFF_SUMMARY_BYTE_LIMIT / this.targets.length);
    const rows = this.targets.map(
      (target) => `'${target.label}|${target.outputFile}'`,
    );

    return [
      {
        name: "Render diff report",
        if: "!cancelled()",
        shell: "bash",
        run: [
          "{",
          "  while IFS='|' read -r label file; do",
          '    echo "## cdk diff — $label"',
          '    echo ""',
          '    if [ ! -f "$file" ]; then',
          "      echo '_Did not run._'",
          '      echo ""',
          "      continue",
          "    fi",
          `    if grep -q '^${DIFF_NEW_STACK_MARKER}' "$file"; then`,
          `      grep '^${DIFF_NEW_STACK_MARKER}' "$file" | sed 's/^/> **/; s/$/**/'`,
          '      echo ""',
          "    fi",
          "    echo '<details><summary>cdk diff output</summary>'",
          '    echo ""',
          "    echo '```'",
          `    if [ "$(wc -c < "$file")" -gt ${budget} ]; then`,
          `      head -c ${budget} "$file"`,
          `      printf '\\n... truncated at ${budget} bytes ...\\n'`,
          "    else",
          '      cat "$file"',
          "    fi",
          "    echo '```'",
          '    echo ""',
          "    echo '</details>'",
          '    echo ""',
          `  done < <(printf '%s\\n' ${rows.join(" ")})`,
          ...(this.artifact
            ? [
                `  echo 'Full diff: the \`${DIFF_ARTIFACT_NAME}\` artifact on [this run](${RUN_URL}).'`,
              ]
            : []),
          '} >> "$GITHUB_STEP_SUMMARY"',
        ].join("\n"),
      },
    ];
  };

  /**
   * Fail the report when any target's diff failed.
   *
   * Runs last, so the summary and the artifact are published first — a reviewer
   * still gets the whole picture, including whatever the failing target managed
   * to capture. But the report must not end up green: everything gated behind
   * it `needs` it, and a job whose `needs` failed is skipped, which is what
   * keeps a failed diff from letting a gated deploy through to *Waiting for
   * approval*. Without this the report would always succeed and a diff failure
   * would only soft-block behind a human.
   *
   * Only `failure` counts. A target skipped by its branch filter reports
   * `skipped`, which is normal and must not fail the report.
   */
  private renderFailureStep = (): Array<JobStep> => {
    const jobNames = Array.from(
      new Set(this.targets.map((target) => target.jobName)),
    );

    if (jobNames.length === 0) {
      return [];
    }

    return [
      {
        name: "Fail if any target's diff failed",
        if: "!cancelled()",
        shell: "bash",
        run: [
          ...jobNames.map(
            (jobName) =>
              `if [ "\${{ needs.${jobName}.result }}" = "failure" ]; then echo "::error::Diff job ${jobName} failed"; failed=1; fi`,
          ),
          'if [ "${failed:-0}" = "1" ]; then exit 1; fi',
        ].join("\n"),
      },
    ];
  };

  /**
   * Upload the merged directory as the one artifact meant to be read.
   */
  private renderUploadStep = (): Array<JobStep> => {
    if (!this.artifact || this.targets.length === 0) {
      return [];
    }

    return [
      WorkflowSteps.uploadArtifact({
        name: "Upload combined diff",
        if: "!cancelled()",
        with: {
          name: DIFF_ARTIFACT_NAME,
          path: DIFF_OUTPUT_DIRECTORY,
          ifNoFilesFound: "ignore",
        },
      }),
    ];
  };
}

/**
 * Upload step for a single target's captured diff, run inside that target's own
 * diff job.
 *
 * Guarded on `!cancelled()` rather than success, so a `cdk diff` that exits
 * non-zero still hands whatever it captured to the report.
 */
export const renderDiffPartUploadStep = (options: {
  label: string;
  artifactName: string;
  outputFile: string;
}): JobStep => {
  return WorkflowSteps.uploadArtifact({
    name: `Upload diff ${options.label}`,
    if: "!cancelled()",
    with: {
      name: options.artifactName,
      path: options.outputFile,
      ifNoFilesFound: "ignore",
      retentionDays: DIFF_PART_RETENTION_DAYS,
    },
  });
};
